import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { subscriptionPtBR, subscriptionEnUS } from "@/features/subscription/i18n";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "pt-BR",
    supportedLngs: ["pt-BR", "en-US"],
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false,
    },
    // Default pt-BR: NÃO usamos `navigator` na detecção (senão um navegador em
    // inglês abriria o portal em en-US). Só persistimos a escolha do toggle no
    // localStorage; sem valor salvo, cai no fallback pt-BR.
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "imobgo.agency.locale",
    },
    react: {
      useSuspense: false,
    },
    // Namespace único `translation` (default do i18next). As chaves são acessadas
    // por prefixo de grupo: t("nav.items.settings"), t("topbar.logout") etc.
    resources: {
      "pt-BR": {
        translation: {
          common: {
            loading: "Carregando...",
            loadingSession: "Carregando sessão...",
            error: "Ocorreu um erro",
          },
          nav: {
            groups: {
              overview: "Visão Geral",
              management: "Gestão",
              analysis: "Análise",
              system: "Sistema",
            },
            items: {
              dashboard: "Dashboard",
              brokers: "Corretores",
              properties: "Imóveis",
              launches: "Lançamentos",
              reports: "Relatórios",
              notifications: "Notificações",
              support: "Suporte",
              settings: "Configurações",
            },
          },
          topbar: {
            search: "Pesquisar...",
            profile: "Perfil",
            logout: "Sair",
          },
          mock: {
            banner: "Ambiente de Demonstração (Mocks: {{modules}})",
          },
          support: {
            title: "Suporte",
            subtitle: "Abra um chamado e acompanhe as respostas da nossa equipe.",
            newTicket: "Abrir chamado",
            myTickets: "Meus chamados",
            loadingList: "Carregando chamados...",
            emptyTitle: "Nenhum chamado ainda",
            emptyDesc:
              "Quando precisar de ajuda, abra um chamado e acompanhe tudo por aqui.",
            updatedAt: "Atualizado em {{date}}",
            openedAt: "Aberto em {{date}}",
            viaPortal: "via Portal",
            detail: {
              loading: "Carregando chamado...",
              back: "Voltar para chamados",
              notFound: "Chamado não encontrado",
              conversation: "Conversa",
              closedNotice:
                "Este chamado está {{status}}. Abra um novo chamado se precisar de mais ajuda.",
              replyPlaceholder: "Escreva sua resposta...",
              reply: "Responder",
              replyError: "Erro ao enviar resposta.",
            },
            modal: {
              title: "Abrir chamado",
              desc: "Descreva sua dúvida ou problema. Nossa equipe de suporte responde por aqui mesmo.",
              subject: "Assunto",
              subjectPlaceholder: "Resumo do chamado",
              category: "Categoria",
              priority: "Prioridade",
              description: "Descrição",
              descriptionPlaceholder:
                "Conte com detalhes o que está acontecendo...",
              cancel: "Cancelar",
              submit: "Abrir chamado",
              created: "Chamado {{code}} aberto com sucesso!",
              createError: "Erro ao abrir chamado. Tente novamente.",
              errSubject: "Descreva o assunto em pelo menos 4 caracteres",
              errDescription: "Detalhe o problema em pelo menos 10 caracteres",
            },
            status: {
              open: "Aberto",
              in_progress: "Em andamento",
              waiting_customer: "Aguardando você",
              resolved: "Resolvido",
              closed: "Encerrado",
            },
            priority: {
              low: "Baixa",
              medium: "Média",
              high: "Alta",
              urgent: "Urgente",
            },
            category: {
              billing: "Faturamento",
              technical: "Técnico",
              account: "Conta",
              feature_request: "Sugestão",
              other: "Outro",
            },
          },
          properties: {
            title: "Imóveis da imobiliária",
            subtitle:
              "Cadastre os imóveis da casa e distribua na carteira dos seus corretores.",
            loading: "Carregando imóveis...",
            new: "Cadastrar Imóvel",
            perMonth: "/mês",
            withWhom: "Com quem está",
            notDistributed: "Ainda não distribuído para nenhum corretor.",
            distributeAction: "Distribuir",
            cycleAction: "Ver ciclo",
            cycle: {
              desc: "Tudo o que aconteceu neste imóvel: propostas, negócios, contratos e vistorias.",
              loading: "Carregando o ciclo do imóvel...",
              neverMoved: "Sem nenhuma movimentação",
              daysWithout_one: "{{count}} dia sem movimentação",
              daysWithout_other: "{{count}} dias sem movimentação",
              visits30d: "Visitas (30d)",
              leads: "Leads",
              openProposals: "Propostas abertas",
              rejected: "Recusadas",
              proposals: "Propostas",
              deals: "Negócios",
              contracts: "Contratos",
              inspections: "Vistorias",
              commission: "comissão",
              rejectedBecause: "Motivo:",
              noProposals: "Nenhuma proposta neste imóvel.",
              noDeals: "Nenhum negócio neste imóvel.",
              noContracts: "Nenhum contrato neste imóvel.",
              noInspections: "Nenhuma vistoria neste imóvel.",
              status: {
                draft: "Rascunho",
                sent: "Enviada",
                negotiating: "Em negociação",
                brokeragreed: "Acordo entre corretores",
                accepted: "Aceita",
                rejected: "Recusada",
                expired: "Expirada",
                canceled: "Cancelada",
                inprogress: "Em andamento",
                closedwon: "Ganho",
                closedlost: "Perdido",
                signed: "Assinado",
                active: "Vigente",
                terminated: "Encerrado",
                scheduled: "Agendada",
                completed: "Concluída",
              },
            },
            revoke: "Tirar da carteira",
            empty: {
              title: "Nenhum imóvel cadastrado",
              desc: "Cadastre um imóvel da casa para distribuir na carteira dos seus corretores. Fotos, vídeos e vitrine ficam disponíveis no CRM depois do cadastro.",
            },
            types: {
              apartment: "Apartamento",
              house: "Casa",
              land: "Terreno",
              commercial: "Comercial",
            },
            operations: { sale: "Venda", rent: "Locação", both: "Venda e locação" },
            assignmentStatus: {
              pending: "Aguardando aceite",
              active: "Na carteira",
              declined: "Recusado",
              revoked: "Revogado",
            },
            form: {
              title: "Cadastrar imóvel da casa",
              desc: "Só o essencial por aqui. Fotos, vídeos, vitrine e vistoria ficam no CRM, no imóvel já criado.",
              titleLabel: "Título do anúncio",
              titleRequired: "Informe um título",
              required: "Campo obrigatório",
              ufInvalid: "Use a sigla com 2 letras",
              typeLabel: "Tipo",
              operationLabel: "Operação",
              salePrice: "Valor de venda",
              rentPrice: "Valor do aluguel",
              street: "Rua",
              number: "Número",
              neighborhood: "Bairro",
              city: "Cidade",
              uf: "UF",
              bedrooms: "Quartos",
              bathrooms: "Banheiros",
              parking: "Vagas",
              area: "Área (m²)",
              mediaHint:
                "Depois de cadastrar, as fotos, os vídeos e a vitrine do imóvel são gerenciados no CRM.",
              cancel: "Cancelar",
              submit: "Cadastrar",
            },
            distribute: {
              title: "Distribuir imóvel",
              desc: "escolha quem vai trabalhar neste imóvel",
              modeLabel: "Como distribuir",
              mode: {
                exclusive: "Responsável único",
                exclusiveDesc:
                  "Um corretor por vez. Distribuir para outro tira o imóvel do anterior.",
                pool: "Carteira aberta",
                poolDesc: "Todos os escolhidos trabalham no mesmo imóvel.",
                team: "Por equipe",
                teamDesc: "Distribuição por equipe.",
              },
              brokersLabel: "Corretores",
              noBrokers: "Nenhum corretor disponível. Convide um corretor primeiro.",
              commissionLabel: "Comissão do parceiro (%)",
              commissionHelp:
                "Obrigatório porque há parceiro externo na seleção — é o percentual que ele recebe neste imóvel.",
              cancel: "Cancelar",
              submit: "Distribuir",
            },
            toast: {
              created: "Imóvel cadastrado!",
              distributed: "Imóvel distribuído na carteira do corretor.",
              revoked: "Imóvel retirado da carteira. O histórico do corretor permanece.",
            },
            errors: {
              "organization.commission_required":
                "Informe o percentual de comissão do corretor parceiro.",
              "organization.broker_not_in_organization":
                "Este corretor não faz parte da sua imobiliária.",
              "organization.property_not_found": "Imóvel não encontrado nesta imobiliária.",
              "crm.unavailable":
                "Não foi possível falar com o sistema dos imóveis. Tente de novo em instantes.",
              default: "Não foi possível concluir a operação.",
            },
          },
          brokers: {
            title: "Corretores",
            subtitle:
              "Convide corretores, libere licenças e acompanhe os seats do seu plano.",
            loading: "Carregando equipe...",
            invite: "Convidar Corretor",
            noSeatsTitle: "Sem seats disponíveis no plano",
            seats: {
              total: "Seats do Plano",
              totalDesc: "Plano: {{plan}}",
              used: "Seats Utilizados",
              available: "Seats Disponíveis",
              availableFull: "Limite do plano atingido",
              availableReady: "Prontos para convidar",
              fullBanner:
                "Você usou todos os seats do plano {{plan}}. Revogue um convite pendente para liberar um seat ou faça upgrade do plano para convidar mais corretores.",
            },
            roster: {
              title: "Equipe da Organização",
              colBroker: "Corretor",
              colEmail: "E-mail",
              colStatus: "Situação",
              colActions: "Ações",
              role: "Corretor",
            },
            status: {
              active: "Ativo",
              pending_license: "Aguardando Licença",
              invited: "Convite Enviado",
            },
            linkType: {
              internal: "Interno",
              internalDesc:
                "Trabalha com exclusividade: o que ele capta entra como imóvel da imobiliária.",
              external: "Parceiro",
              externalDesc:
                "Parceria sem exclusividade: a carteira própria dele continua privada.",
            },
            actions: {
              resend: "Reenviar",
              revoke: "Revogar",
              activate: "Liberar Licença",
              inTeam: "Na equipe",
            },
            toast: {
              resent: "Convite reenviado.",
              resendError: "Erro ao reenviar convite.",
              revoked: "Convite revogado. O seat voltou para o seu plano.",
              revokeError: "Erro ao revogar convite.",
              activated: "Licença liberada! O corretor já está ativo.",
              activateError: "Erro ao liberar licença.",
              invited: "Convite enviado! Um seat do seu plano foi reservado.",
              inviteError: "Erro ao enviar convite.",
              rejected: {
                "agency.broker_already_linked":
                  "Este corretor já faz parte de outra imobiliária. O seat continua disponível no seu plano.",
                "agency.broker_already_on_platform":
                  "Este e-mail já tem conta no ImobGo. Por enquanto só é possível convidar quem ainda não usa a plataforma.",
                default:
                  "Não foi possível enviar o convite. Confira o e-mail informado.",
              },
            },
            modal: {
              title: "Convidar Corretor",
              desc: "Envie um convite por e-mail para que o corretor se cadastre e faça parte da sua equipe no ImobGo. O convite reserva um seat do seu plano.",
              emailLabel: "E-mail do Corretor",
              emailPlaceholder: "corretor@email.com",
              emailInvalid: "E-mail inválido",
              linkTypeLabel: "Como ele trabalha com você",
              linkTypeRequired: "Escolha como o corretor trabalha com você",
              cancel: "Cancelar",
              submit: "Enviar Convite",
            },
          },
        },
      },
      "en-US": {
        translation: {
          common: {
            loading: "Loading...",
            loadingSession: "Loading session...",
            error: "Something went wrong",
          },
          nav: {
            groups: {
              overview: "Overview",
              management: "Management",
              analysis: "Analysis",
              system: "System",
            },
            items: {
              dashboard: "Dashboard",
              brokers: "Brokers",
              properties: "Listings",
              reports: "Reports",
              notifications: "Notifications",
              support: "Support",
              settings: "Settings",
            },
          },
          topbar: {
            search: "Search...",
            profile: "Profile",
            logout: "Sign out",
          },
          mock: {
            banner: "Demo Environment (Mocks: {{modules}})",
          },
          support: {
            title: "Support",
            subtitle: "Open a ticket and follow up on our team's replies.",
            newTicket: "Open ticket",
            myTickets: "My tickets",
            loadingList: "Loading tickets...",
            emptyTitle: "No tickets yet",
            emptyDesc:
              "Whenever you need help, open a ticket and track everything here.",
            updatedAt: "Updated {{date}}",
            openedAt: "Opened {{date}}",
            viaPortal: "via Portal",
            detail: {
              loading: "Loading ticket...",
              back: "Back to tickets",
              notFound: "Ticket not found",
              conversation: "Conversation",
              closedNotice:
                "This ticket is {{status}}. Open a new ticket if you need more help.",
              replyPlaceholder: "Write your reply...",
              reply: "Reply",
              replyError: "Failed to send reply.",
            },
            modal: {
              title: "Open ticket",
              desc: "Describe your question or issue. Our support team will reply right here.",
              subject: "Subject",
              subjectPlaceholder: "Ticket summary",
              category: "Category",
              priority: "Priority",
              description: "Description",
              descriptionPlaceholder: "Tell us in detail what's happening...",
              cancel: "Cancel",
              submit: "Open ticket",
              created: "Ticket {{code}} opened successfully!",
              createError: "Failed to open ticket. Please try again.",
              errSubject: "Describe the subject in at least 4 characters",
              errDescription: "Describe the issue in at least 10 characters",
            },
            status: {
              open: "Open",
              in_progress: "In progress",
              waiting_customer: "Waiting on you",
              resolved: "Resolved",
              closed: "Closed",
            },
            priority: {
              low: "Low",
              medium: "Medium",
              high: "High",
              urgent: "Urgent",
            },
            category: {
              billing: "Billing",
              technical: "Technical",
              account: "Account",
              feature_request: "Feature request",
              other: "Other",
            },
          },
          properties: {
            title: "Agency listings",
            subtitle: "Register the agency listings and assign them to your brokers.",
            loading: "Loading listings...",
            new: "New listing",
            perMonth: "/month",
            withWhom: "Assigned to",
            notDistributed: "Not assigned to any broker yet.",
            distributeAction: "Assign",
            cycleAction: "View cycle",
            cycle: {
              desc: "Everything that happened on this listing: offers, deals, contracts and inspections.",
              loading: "Loading the listing cycle...",
              neverMoved: "No activity at all",
              daysWithout_one: "{{count}} day without activity",
              daysWithout_other: "{{count}} days without activity",
              visits30d: "Visits (30d)",
              leads: "Leads",
              openProposals: "Open offers",
              rejected: "Rejected",
              proposals: "Offers",
              deals: "Deals",
              contracts: "Contracts",
              inspections: "Inspections",
              commission: "commission",
              rejectedBecause: "Reason:",
              noProposals: "No offers on this listing.",
              noDeals: "No deals on this listing.",
              noContracts: "No contracts on this listing.",
              noInspections: "No inspections on this listing.",
              status: {
                draft: "Draft",
                sent: "Sent",
                negotiating: "Negotiating",
                brokeragreed: "Brokers agreed",
                accepted: "Accepted",
                rejected: "Rejected",
                expired: "Expired",
                canceled: "Canceled",
                inprogress: "In progress",
                closedwon: "Won",
                closedlost: "Lost",
                signed: "Signed",
                active: "Active",
                terminated: "Terminated",
                scheduled: "Scheduled",
                completed: "Completed",
              },
            },
            revoke: "Remove from wallet",
            empty: {
              title: "No listings yet",
              desc: "Register a listing to assign it to your brokers. Photos, videos and the showcase are managed in the CRM after creation.",
            },
            types: {
              apartment: "Apartment",
              house: "House",
              land: "Land",
              commercial: "Commercial",
            },
            operations: { sale: "Sale", rent: "Rent", both: "Sale and rent" },
            assignmentStatus: {
              pending: "Awaiting acceptance",
              active: "In wallet",
              declined: "Declined",
              revoked: "Revoked",
            },
            form: {
              title: "New agency listing",
              desc: "Just the essentials here. Photos, videos, showcase and inspections live in the CRM.",
              titleLabel: "Listing title",
              titleRequired: "Enter a title",
              required: "Required field",
              ufInvalid: "Use the 2-letter state code",
              typeLabel: "Type",
              operationLabel: "Operation",
              salePrice: "Sale price",
              rentPrice: "Rent price",
              street: "Street",
              number: "Number",
              neighborhood: "Neighborhood",
              city: "City",
              uf: "State",
              bedrooms: "Bedrooms",
              bathrooms: "Bathrooms",
              parking: "Parking",
              area: "Area (m²)",
              mediaHint: "After creating, photos, videos and the showcase are managed in the CRM.",
              cancel: "Cancel",
              submit: "Create",
            },
            distribute: {
              title: "Assign listing",
              desc: "choose who will work on it",
              modeLabel: "How to assign",
              mode: {
                exclusive: "Single owner",
                exclusiveDesc:
                  "One broker at a time. Assigning to another removes it from the previous one.",
                pool: "Open wallet",
                poolDesc: "Everyone selected works on the same listing.",
                team: "By team",
                teamDesc: "Assignment by team.",
              },
              brokersLabel: "Brokers",
              noBrokers: "No brokers available. Invite a broker first.",
              commissionLabel: "Partner commission (%)",
              commissionHelp:
                "Required because a partner broker is selected — it is what they get on this listing.",
              cancel: "Cancel",
              submit: "Assign",
            },
            toast: {
              created: "Listing created!",
              distributed: "Listing assigned to the broker.",
              revoked: "Listing removed from the wallet. The broker history stays with them.",
            },
            errors: {
              "organization.commission_required": "Enter the partner broker commission.",
              "organization.broker_not_in_organization": "This broker is not part of your agency.",
              "organization.property_not_found": "Listing not found in this agency.",
              "crm.unavailable": "Could not reach the listings service. Try again shortly.",
              default: "Could not complete the operation.",
            },
          },
          brokers: {
            title: "Brokers",
            subtitle:
              "Invite brokers, release licenses and keep track of your plan seats.",
            loading: "Loading team...",
            invite: "Invite broker",
            noSeatsTitle: "No seats available in the plan",
            seats: {
              total: "Plan seats",
              totalDesc: "Plan: {{plan}}",
              used: "Used seats",
              available: "Available seats",
              availableFull: "Plan limit reached",
              availableReady: "Ready to invite",
              fullBanner:
                "You've used all seats in the {{plan}} plan. Revoke a pending invite to free a seat or upgrade your plan to invite more brokers.",
            },
            roster: {
              title: "Organization team",
              colBroker: "Broker",
              colEmail: "Email",
              colStatus: "Status",
              colActions: "Actions",
              role: "Broker",
            },
            status: {
              active: "Active",
              pending_license: "Awaiting license",
              invited: "Invite sent",
            },
            linkType: {
              internal: "In-house",
              internalDesc:
                "Works exclusively with you: whatever they list belongs to the agency.",
              external: "Partner",
              externalDesc:
                "Partnership without exclusivity: their own portfolio stays private.",
            },
            actions: {
              resend: "Resend",
              revoke: "Revoke",
              activate: "Release license",
              inTeam: "On the team",
            },
            toast: {
              resent: "Invite resent.",
              resendError: "Failed to resend invite.",
              revoked: "Invite revoked. The seat is back on your plan.",
              revokeError: "Failed to revoke invite.",
              activated: "License released! The broker is now active.",
              activateError: "Failed to release license.",
              invited: "Invite sent! A seat on your plan has been reserved.",
              inviteError: "Failed to send invite.",
              rejected: {
                "agency.broker_already_linked":
                  "This broker already belongs to another agency. The seat is still available on your plan.",
                "agency.broker_already_on_platform":
                  "This email already has an ImobGo account. For now you can only invite brokers who don't use the platform yet.",
                default: "Could not send the invite. Check the email address.",
              },
            },
            modal: {
              title: "Invite broker",
              desc: "Send an email invite so the broker can sign up and join your team on ImobGo. The invite reserves a seat on your plan.",
              emailLabel: "Broker's email",
              emailPlaceholder: "broker@email.com",
              emailInvalid: "Invalid email",
              linkTypeLabel: "How they work with you",
              linkTypeRequired: "Choose how the broker works with you",
              cancel: "Cancel",
              submit: "Send invite",
            },
          },
        },
      },
    },
  });

// Strings da feature de assinatura (hub#138) — agregadas ao namespace `translation`
// sob a chave `subscription`. Mantém o arquivo grande da feature fora deste setup.
i18n.addResourceBundle("pt-BR", "translation", { subscription: subscriptionPtBR }, true, true);
i18n.addResourceBundle("en-US", "translation", { subscription: subscriptionEnUS }, true, true);

export default i18n;
